// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useEffect, useState, useRef, Fragment } from 'react'
import { getI18n, getI18nFormattedMessage } from '../../../utils/intl.js'
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core';
import { useParams } from 'react-router';
import { findIndex, get } from 'lodash';
import { getIsPortalAdmin } from '../../../reducers/portalConfig/index.js';
import styles from './styles';
import * as CONSTANTS from '../../../constants';
import {
  FormActionButtons,
  TabPanel,
  TabsContainer,
  DetailsHeader,
} from '../../../components';

import {
  getAuthProvider,
  getCreateAuthProviderError,
  getCreateAuthProviderStatus,
  getCreatedAuthProvider,
  getDeleteAuthProviderError,
  getDeleteAuthProviderStatus,
  getIsAuthProviderLoading,
  getUpdateAuthProviderError,
  getUpdateAuthProviderStatus,
  isCreatingAuthProvider,
  isUpdatingAuthProvider,
  isDeletingAuthProvider,
  getClientDefintionsList,
  getListClientDefintionsInProgress,
  getClientDefintionsListError,
  getAuthProviderError,
} from '../../../reducers/authProviders/index.js';

import {
  createAuthProvider,
  deleteAuthProvider,
  fetchAuthProvider,
  resetAuthProviderEditStatuses,
  updateAuthProvider,
  fetchClientDefinitions,
} from '../../../actions/authProviders';

import {
  ROUTES,
} from '../../../constants/routes';

import DetailsContainer from '../../details.js';
import { AuthProviderOverview } from './Overview/index.js';
import ListAuthProviderClientDefinitions from './ListClientDefinitions/index.js';

export const AuthProviderDetailsContainer = ({
  match,
  history,
  isPortalAdmin,
  pathName,
  isLoadingAuthProvider,
  authProviderDetails,
  loadAuthProviderError,
  createAuthProvider,
  createdAuthProvider,
  createAuthProviderError,
  createAuthProviderStatus,
  deleteAuthProvider,
  deleteAuthProviderError,
  deleteAuthProviderStatus,
  fetchAuthProvider,
  isCreatingAuthProvider,
  isDeletingAuthProvider,
  isUpdatingAuthProvider,
  resetAuthProviderEditStatuses,
  updateAuthProvider,
  updateAuthProviderError,
  updateAuthProviderStatus,
  definitions,
  isLoadingDefinitions,
  fetchClientDefinitions,
  errorLoadingDefinitions,
}) => {

  const intl = getI18n(useIntl());
  const params = useParams();
  const authProviderUuid = params?.authProviderUuid;
  const isEditMode = !!authProviderUuid;
  const [isFieldEdit, setIsFieldEdit] = useState(!isEditMode);

  // auth provider name common to both tabs
  const [isUnsavedAlertDialogOpen, setIsUnsavedAlertDialogOpen] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationStatus, setNotificationStatus] = useState('');
  const [serverFieldErrors, setServerFieldErrors] = useState([]);

  const overviewTabId = CONSTANTS.AUTH_PROVIDER_OVERVIEW_TAB_ID;
  const scdTabId = CONSTANTS.AUTH_PROVIDER_CLIENT_DEF_TAB_ID;

  const AUTH_PROVIDER_NAME_MAX_LENGTH = 255;
  const AUTH_PROVIDER_ISSUER_MAX_LENGTH = 2048;
  const AUTH_HEADER_MAX_LENGTH = 2048;
  const SANITIZE_FIELDS_MAX_LENGTH = 2048;

  const getOverviewTab = () => {
    return {
      id: overviewTabId,
      tabId: overviewTabId,
      label: getI18nFormattedMessage('label.auth.providers.details.overview.tab.title'),
      visible: true,
    };
  }
  const getClientDefinitionsTab = () => {
    return {
      id: scdTabId,
      tabId: scdTabId,
      label: getI18nFormattedMessage('label.auth.providers.details.sample.client.definition.tab.title'),
      visible: true,
      disabled: !authProviderUuid,
    }
  }
  const tabItems = [
    getOverviewTab(),
    getClientDefinitionsTab(),
  ];
  const getTabs = (tabItems) => tabItems.filter(({ visible }) => visible);
  const getSelectedTabIndex = (tabs, match) => {
    const tabName = get(match, 'params.tabName');
    const index = findIndex(tabs, tab => (tab.id === tabName));
    // if none found default to 0
    return (index > 0 ? index : 0);
  };

  const [tabs, setTabs] = useState(getTabs(tabItems));
  const defaultIndex = getSelectedTabIndex(tabs, match);
  const [tabIndex, setTabIndex] = useState(defaultIndex);
  const [currentTab, setCurrentTab] = useState(tabs[defaultIndex].tabId);
  const [refreshClientDefinitions, setRefreshClientDefinitions] = useState(false);
  // used to navigate correctly with unsaved changes between history and
  // other tab selection
  const tempTabIndex = useRef(defaultIndex);

  // seggregate the data tabswise
  const payload = useRef({
    uuid: null,
    providerName: '',
    issuerName: '',
    clientRegistrationType: CONSTANTS.AUTH_PROVIDER_TYPE_EXTERNAL,
    authHeader: '',
    isDcr: false,
    sanitizedFields: '',
  });

  // capture the form state for auth provider tab
  const [formState, setFormState] = useState({
    errors: {},
    isDirty: false,
  });
  // Auth provider name is in a separate header component, different to
  // both tabs
  const [providerName, setProviderName] = useState('');

  useEffect(() => {
    if (!isPortalAdmin) {
      history.push('/404');
    }
  }, [isPortalAdmin]);

  // api calls for the selected tab
  useEffect(() => {
    if (authProviderUuid) {
      fetchAuthProvider(authProviderUuid);
      fetchClientDefinitions(authProviderUuid);
    }
    // clear off all the redux state for both auth providers and client definitions
    return () => {
      resetAuthProviderEditStatuses();
    };
  }, []);

  useEffect(() => {
    if (authProviderDetails) {
      populateOverviewDetails(authProviderDetails);
    }
  }, [authProviderDetails]);

  useEffect(() => {
    if (loadAuthProviderError) {
      setNotificationStatus('error')
      setNotificationMessage(loadAuthProviderError || intl.getI18nMessage('label.unknown.error'));
    }
  }, [loadAuthProviderError]);

  useEffect(() => {
    if (createAuthProviderStatus === CONSTANTS.SUCCESS) {
      setNotificationStatus('success');
      setNotificationMessage(intl.getI18nMessage('label.auth.providers.details.creation.success.message'));
      setIsFieldEdit(false);
      resetForm();
      setTimeout(() => {
        history.push(`/admin/settings/application-settings/auth-providers/${createdAuthProvider.uuid}/edit/${currentTab}`);
        clearMessage();
        setTabs([
          getOverviewTab(),
          {
            ...getClientDefinitionsTab(),
            disabled: !payload.current.isDcr,
          },
        ])
      }, 2500);
    } else if (createAuthProviderStatus === CONSTANTS.FAIL) {
      setServerFieldErrors(getSeverErrorFields(createAuthProviderError));
      setNotificationStatus('error');
      setNotificationMessage(getUIListInCaseOfMultiErrors(createAuthProviderError) || intl.getI18nMessage('label.unknown.error'));
    }
  }, [createAuthProviderStatus]);

  useEffect(() => {
    if (updateAuthProviderStatus === CONSTANTS.SUCCESS) {
      setNotificationStatus('success');
      setNotificationMessage(intl.getI18nMessage('label.auth.providers.details.update.success.message'));
      resetForm();
      setIsFieldEdit(false);
      setTimeout(() => {
        clearMessage();
      }, 2500);
    } else if (updateAuthProviderStatus === CONSTANTS.FAIL) {
      setServerFieldErrors(getSeverErrorFields(updateAuthProviderError));
      setNotificationStatus('error')
      setNotificationMessage(getUIListInCaseOfMultiErrors(updateAuthProviderError) || intl.getI18nMessage('label.unknown.error'));
    }
  }, [updateAuthProviderStatus]);

  useEffect(() => {
    if (deleteAuthProviderStatus === CONSTANTS.SUCCESS) {
      setNotificationStatus('success');
      setNotificationMessage(intl.getI18nMessage('label.auth.providers.details.delete.success.message'))
      resetForm();
      setTimeout(() => {
        clearMessage();
        history.push(ROUTES.authProvidersList.path);
      }, 2500);
    } else if (deleteAuthProviderStatus === CONSTANTS.FAIL) {
      setNotificationStatus('error')
      setNotificationMessage(deleteAuthProviderError || intl.getI18nMessage('label.unknown.error'));
    }
  }, [deleteAuthProviderStatus]);

  useEffect(() => {
    if (errorLoadingDefinitions) {
      // we have to show errors in loading client definitions in the main page header
      setNotificationStatus('error');
      setNotificationMessage(errorLoadingDefinitions || intl.getI18nMessage('label.unknown.error'));
      setIsFieldEdit(false);
    }
  }, [errorLoadingDefinitions]);

  useEffect(() => {
    if (refreshClientDefinitions) {
      fetchClientDefinitions(authProviderUuid);
      setRefreshClientDefinitions(false);
    }
  }, [refreshClientDefinitions]);

  const refreshClientDefinitionsHandler = () => {
    setRefreshClientDefinitions(true);
  }

  const getUIListInCaseOfMultiErrors = (message) => {
    if (Array.isArray(message) && message?.length) {
      return message.length > 1 ? (<ul>{message.map((item, index) => {
        return <li key={index}>{item?.field ? `${item.field} : ${item.error}` : `${item.error}`}</li>
      })}</ul>) :
        message[0].field ? `${message[0].field} : ${message[0].error}` : `${message[0].error || message}`;
    } else {
      return message;
    }
  }

  const getSeverErrorFields = (message) => {
    const fields = [];
    if (Array.isArray(message) && message?.length) {
      if (message.length > 1) {
        message.map((item) => {
          fields.push(item?.field);
        });
      } else {
        fields.push(message[0].field);
      }
    }
    return fields;
  }

  const populateOverviewDetails = (details) => {
    setProviderName(details.name)
    payload.current = {
      uuid: details?.uuid || null,
      providerName: details?.name || '',
      issuerName: details?.issuer || '',
      clientRegistrationType: details?.clientRegistrationType,
      authHeader: details?.providerConfiguration?.authorization?.authorizationHeader || '',
      isDcr: details?.clientRegistrationType === CONSTANTS.AUTH_PROVIDER_TYPE_PORTAL_DCR,
      sanitizedFields: details?.providerConfiguration?.clientMetadataSensitiveFields?.join(',') || '',
    }
    // disable the client definitions tab if the
    // auth provider is not portal managed
    setTabs(
      [
        getOverviewTab(),
        {
          ...getClientDefinitionsTab(),
          disabled: details?.clientRegistrationType !== CONSTANTS.AUTH_PROVIDER_TYPE_PORTAL_DCR,
        },
      ],
    );
  }

  const clearMessage = () => {
    setNotificationMessage("")
    setNotificationStatus("");
    setServerFieldErrors([]);
  };

  const switchTab = (newValue) => {
    const newTabId = tabs[newValue].tabId;
    setTabIndex(newValue);
    setCurrentTab(newTabId);
    setIsFieldEdit(false);
    history.push(`/admin/settings/application-settings/auth-providers/${authProviderUuid}/edit/${newTabId}`);
  }

  const handleTabChange = (e, newValue) => {
    tempTabIndex.current = newValue;
    // disable routing due to overview tab click if opened in create mode
    if (!authProviderUuid) {
      e.preventDefault();
      e.stopPropagation();
    } else if (isFormDirty()) {
      setIsUnsavedAlertDialogOpen(true);
    } else {
      switchTab(newValue);
    }
  };

  const handleProviderNameChange = (name) => {
    setProviderName(name);
    if (validateAndSetPayload(null, name, 'providerName', AUTH_PROVIDER_NAME_MAX_LENGTH)) {
      payload.current.providerName = name;
    }
  }

  const handleIssuerChange = (issuer) => {
    if (validateAndSetPayload(null, issuer, 'issuerName', AUTH_PROVIDER_ISSUER_MAX_LENGTH)) {
      payload.current.issuerName = issuer;
    }
  }

  const handleAuthHeaderChange = (header) => {
    if (validateAndSetPayload(null, header, 'authHeader', AUTH_HEADER_MAX_LENGTH)) {
      payload.current.authHeader = header;
    }
  }

  const handleIsDcrChange = (dcr) => {
    payload.current.isDcr = dcr;
    markFormDirty();
  }

  const markFormDirty = () => {
    setFormState((formState) => {
      return {
        ...formState,
        isDirty: true,
      }
    })
  }

  const resetForm = () => {
    setFormState((formState) => {
      return {
        ...formState,
        errors: {},
        isDirty: false,
      }
    })
  }

  const handleSanitizeFieldsChange = (fields) => {
    if (validateAndSetPayload(null, fields, 'sanitizedFields', SANITIZE_FIELDS_MAX_LENGTH)) {
      payload.current.sanitizedFields = fields;
    }
  }

  const handleDeleteAuthProvider = () => {
    deleteAuthProvider(authProviderUuid);
  };

  const onUnsavedAlertDialogClose = () => {
    setIsUnsavedAlertDialogOpen(false);
    tempTabIndex.current = getSelectedTabIndex(tabs, match);
  };


  const onUnsavedAlertDialogSubmit = () => {
    // Identify if dialog is due to unsaved tab changes or page cancel
    if (tempTabIndex.current !== -1) {
      setIsUnsavedAlertDialogOpen(false);
      setIsFieldEdit(false);
      resetForm();
      switchTab(tempTabIndex.current);
      // since the route change is not triggering refresh, we have to reset the
      // form state and data manually in case of unchanged changes in overview tab
      fetchAuthProvider(authProviderUuid);
    } else {
      setIsUnsavedAlertDialogOpen(false);
      history.push(ROUTES.authProvidersList.path);
    }
  };

  const isFormDirty = () => {
    return formState.isDirty;
  }

  // prepare payload based on the tab selected
  const preparePayload = () => {
    let payloadToSave = {
      uuid: authProviderUuid || null,
      issuer: payload.current?.issuerName?.trim(),
      name: payload.current?.providerName?.trim(),
    };

    if (payload.current.isDcr) {
      payloadToSave = {
        ...payloadToSave,
        clientRegistrationType: CONSTANTS.AUTH_PROVIDER_TYPE_PORTAL_DCR,
        providerConfiguration: {
          clientMetadataSensitiveFields: payload.current.sanitizedFields?.split(",")
            ?.filter((field) => field.trim().length).map((field) => field.trim()),
          authorization: {
            authorizationHeader: payload.current.authHeader?.trim() || '',
          },
        },
      }
    } else if (payload.current.clientRegistrationType === CONSTANTS.AUTH_PROVIDER_TYPE_PORTAL) {
      payloadToSave = {
        ...payloadToSave,
        clientRegistrationType: CONSTANTS.AUTH_PROVIDER_TYPE_PORTAL,
      }
    } else {
      payloadToSave = {
        ...payloadToSave,
        clientRegistrationType: CONSTANTS.AUTH_PROVIDER_TYPE_EXTERNAL,
      }
    }
    return payloadToSave;
  }

  // save should call different dispatch actions based
  // on the tab selected
  const handleSave = (isUpdate) => {
    clearMessage();
    const payloadToSave = preparePayload();
    if (isUpdate) {
      updateAuthProvider(payloadToSave, authProviderUuid);
    } else {
      createAuthProvider(payloadToSave);
    }
  }

  // showing unsaved data warning at the page level(not tab) makes sense only for edit
  const handleCancelForPage = () => {
    tempTabIndex.current = -1;
    if (isEditMode) {
      if (isFormDirty()) {
        setIsUnsavedAlertDialogOpen(true);
      } else {
        setIsUnsavedAlertDialogOpen(false);
        history.push(ROUTES.authProvidersList.path);
        // history.goBack();
      }
    } else {
      setIsUnsavedAlertDialogOpen(false);
      history.push(ROUTES.authProvidersList.path);
      // history.goBack();
    }
  };

  const isFieldValid = (val, field) => {
    return Boolean(val) && !formState.errors[field];
  };

  const canSave = () => {
    // As per new ui changes, we should handle save from here only for auth providers
    // for client definitions it will be handled from the popup
    return formState.isDirty &&
      isFieldValid(payload.current.providerName, 'providerName') &&
      isFieldValid(payload.current.issuerName, 'issuerName');
  }

  // UI validations
  const validateField = (regex, value, field, maxlength) => {
    let isValid = false;
    if (!value) {
      setFormState((formState) => {
        return {
          ...formState,
          isDirty: true,
          errors: {
            ...formState.errors,
            [field]: `${field} ${intl.getI18nMessage('label.auth.providers.details.empty.message')}`,
          },
        }
      });
    } else if (value.length > maxlength) {
      setFormState((formState) => {
        return {
          ...formState,
          isDirty: true,
          errors: {
            ...formState.errors,
            [field]: `${field} ${intl.getI18nMessage('label.auth.providers.details.invalid.length.message')}`,
          },
        }
      });
    } else if (regex?.test(value)) {
      setFormState((formState) => {
        return {
          ...formState,
          isDirty: true,
          errors: {
            ...formState.errors,
            [field]: `${field} ${intl.getI18nMessage('label.auth.providers.details.invalid.chars.message')}`,
          },
        }
      });
    } else {
      setFormState((formState) => {
        return {
          ...formState,
          isDirty: true,
          errors: {
            ...formState.errors,
            [field]: null,
          },
        }
      });
      isValid = true;
    }
    return isValid;
  };

  const validateAndSetPayload = (regex, value, field, maxlength) => {
    if (validateField(regex, value, field, Number(maxlength))) {
      payload.current[field] = value;
      return true;
    }
    return false;
  };

  const showLoadingOverlay = (isLoadingAuthProvider ||
    isCreatingAuthProvider ||
    isUpdatingAuthProvider ||
    isDeletingAuthProvider);

  const header = (
    <DetailsHeader
      headerId={'auth-provider-header'}
      deleteFn={handleDeleteAuthProvider}
      handleChange={handleProviderNameChange}
      helperText={intl.getI18nMessage("label.auth.providers.name.helper.text")}
      isFieldEdit={isFieldEdit}
      isFieldEditIcon={currentTab === overviewTabId}
      maxLength={AUTH_PROVIDER_NAME_MAX_LENGTH}
      name={providerName}
      onClickEditIcon={() => setIsFieldEdit(true)}
      placeHolderText={intl.getI18nMessage("label.auth.providers.details.name.place.holder")}
      showDelete={isEditMode &&
        currentTab === overviewTabId &&
        deleteAuthProviderStatus !== CONSTANTS.SUCCESS
      }
      type={"textField"}
      showBreadCrumbs={true}
      pathName={pathName}
      isNameError={serverFieldErrors.some((field)=> field.trim().toLowerCase().startsWith('name'))}
    />);

  return (
    <DetailsContainer
      detailsPageId="auth-provider-details-page"
      isLoading={showLoadingOverlay}
      notificationId="auth-provider-details-notifications"
      notificationStatus={notificationStatus}
      setNotificationStatus={setNotificationStatus}
      notificationMessage={notificationMessage}
      setNotificationMessage={setNotificationMessage}
      isActionsEnabled={false}
      header={header}
      footer=
      {currentTab === overviewTabId && <FormActionButtons
        assistText={intl.getI18nMessage("label.auth.providers.details.footer.assist.text")}
        id={"appBar"}
        isContainerFooter
        showCancel
        cancelText={intl.getI18nMessage("label.cancel.button")}
        onCancelClick={handleCancelForPage}
        showNext
        nextText={intl.getI18nMessage("label.save.button")}
        onNextClick={() => handleSave(isEditMode)}
        isDisabled={!canSave()}
      />}
      tabs={
        <TabsContainer
          id={'auth-provider-details-tab-container'}
          tabValue={tabIndex}
          orientation="horizontal"
          tabItems={tabs}
          handleTabChange={handleTabChange}
        />
      }
      page={
        <Fragment>
          <TabPanel
            visible={currentTab === overviewTabId}
            id="tab-auth-provider-overview"
          >
            <AuthProviderOverview
              authProviderUuid={authProviderUuid}
              issuerName={authProviderDetails?.issuer}
              clientRegistrationType={authProviderDetails?.clientRegistrationType}
              authHeader={authProviderDetails?.providerConfiguration?.authorization?.authorizationHeader || ''}
              sanitizedFields={authProviderDetails?.providerConfiguration?.clientMetadataSensitiveFields?.join(',')}
              issuerChangeCallback={handleIssuerChange}
              authHeaderChangeCallback={handleAuthHeaderChange}
              dcrChangeCallback={handleIsDcrChange}
              sanitizedFieldsCallback={handleSanitizeFieldsChange}
              errors={serverFieldErrors}
            />
          </TabPanel>
          <TabPanel
            visible={currentTab === scdTabId}
            id="tab-auth-provider-sample-client-definition"
          >
            <ListAuthProviderClientDefinitions
              authProviderUuid={authProviderUuid}
              definitions={definitions}
              isLoadingDefinitions={isLoadingDefinitions}
              refreshClientDefinitionsCallback={refreshClientDefinitionsHandler}
            />
          </TabPanel>
        </Fragment>
      }
      isDialogOpen={isUnsavedAlertDialogOpen}
      dialogSubmitText={intl.getI18nMessage("label.dialog.unsaved.changes.submit")}
      onDialogClose={onUnsavedAlertDialogClose}
      onDialogSubmit={onUnsavedAlertDialogSubmit}
    />
  );
};

const mapStateToProps = state => ({
  authProviderDetails: getAuthProvider(state),
  loadAuthProviderError: getAuthProviderError(state),
  createdAuthProvider: getCreatedAuthProvider(state),
  createAuthProviderError: getCreateAuthProviderError(state),
  createAuthProviderStatus: getCreateAuthProviderStatus(state),
  deleteAuthProviderError: getDeleteAuthProviderError(state),
  deleteAuthProviderStatus: getDeleteAuthProviderStatus(state),
  isCreatingAuthProvider: isCreatingAuthProvider(state),
  isDeletingAuthProvider: isDeletingAuthProvider(state),
  isUpdatingAuthProvider: isUpdatingAuthProvider(state),
  isLoadingAuthProvider: getIsAuthProviderLoading(state),
  isPortalAdmin: getIsPortalAdmin(state),
  updateAuthProviderError: getUpdateAuthProviderError(state),
  updateAuthProviderStatus: getUpdateAuthProviderStatus(state),
  definitions: getClientDefintionsList(state),
  isLoadingDefinitions: getListClientDefintionsInProgress(state),
  errorLoadingDefinitions: getClientDefintionsListError(state),
  pathName: get(state, 'router.location.pathname'),
});

const mapDispatchToProps = {
  createAuthProvider,
  deleteAuthProvider,
  fetchAuthProvider,
  resetAuthProviderEditStatuses,
  updateAuthProvider,
  fetchClientDefinitions,
};

AuthProviderDetailsContainer.propTypes = {
  history: PropTypes.object,
  classes: PropTypes.object,
  loadAuthProviderError: PropTypes.string,
  isLoadingAuthProvider: PropTypes.bool,
  isPortalAdmin: PropTypes.bool,
  authProviderDetails: PropTypes.object,
  createAuthProviderError: PropTypes.array,
  createAuthProvider: PropTypes.func,
  createdAuthProvider: PropTypes.object,
  createAuthProviderStatus: PropTypes.string,
  deleteAuthProvider: PropTypes.func,
  deleteAuthProviderError: PropTypes.string,
  deleteAuthProviderStatus: PropTypes.string,
  fetchAuthProvider: PropTypes.func,
  isCreatingAuthProvider: PropTypes.bool,
  isDeletingAuthProvider: PropTypes.bool,
  isUpdatingAuthProvider: PropTypes.bool,
  resetAuthProviderEditStatuses: PropTypes.func,
  updateAuthProvider: PropTypes.func,
  updateAuthProviderError: PropTypes.array,
  updateAuthProviderStatus: PropTypes.string,
  match: PropTypes.shape({
    params: PropTypes.shape({
      tabId: PropTypes.string,
    }),
  }),
  pathName: PropTypes.string,
  definitions: PropTypes.arrayOf(PropTypes.object),
  isLoadingDefinitions: PropTypes.bool,
  errorLoadingDefinitions: PropTypes.object,
  fetchClientDefinitions: PropTypes.func,
};

export default (
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps)
)(AuthProviderDetailsContainer);
